/**
 * WordStatesRecapModal — web port of
 * client-native/src/features/tutorial/components/WordStatesRecapModal.tsx.
 *
 * A popup shown after the word-click phase of the in-feed tutorial completes
 * (iOS FeedNew.tsx:1392 setShowRecapModal(true)). It recaps the three word
 * states (new / learning / known) before moving to the phrase-highlight phase.
 * A 3s countdown gates the "Got it!" button and backdrop dismissal, matching
 * the native modal's COUNTDOWN_SECONDS gate.
 */
import { useEffect, useState } from "react";
import { colors } from "@/ui";
import ContinueButton from "@/components/Buttons/ContinueButton";

const COUNTDOWN_SECONDS = 3;

// Word state colors — mirror the transcript pills: new = yellow, learning =
// light yellow, known = the dark-gray "got it" chip (rgba(44,44,46,0.7)) with
// white text, NOT plain/transparent — a known word reads as a solid dark chip.
const WORD_COLORS: Record<WordState, string> = {
  new: colors.yellow, // #fbba00
  learning: colors.lightYellow, // #FFEBA1
  known: "rgba(44,44,46,0.7)", // BG_KNOWN (Transcript.tsx)
};

type WordState = "new" | "learning" | "known";

interface WordStatesRecapModalProps {
  isVisible: boolean;
  onContinue: () => void;
}

function WordStateRow({
  word,
  state,
  label,
}: {
  word: string;
  state: WordState;
  label: string;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16 }}>
      <div style={{ width: 80, display: "flex", justifyContent: "flex-end" }}>
        <span
          style={{
            backgroundColor: WORD_COLORS[state],
            paddingInline: 16,
            paddingBlock: 8,
            borderRadius: 8,
            fontSize: "var(--base-font-size-large)",
            fontWeight: 700,
            // Known is a dark chip → white text; new/learning are light → dark text.
            color: state === "known" ? colors.white : colors.text.primary,
          }}
        >
          {word}
        </span>
      </div>
      <div style={{ width: 100, display: "flex", justifyContent: "flex-start" }}>
        <span style={{ fontSize: "var(--base-font-size-base)", color: colors.text.primary }}>
          {label}
        </span>
      </div>
    </div>
  );
}

const WordStatesRecapModal: React.FC<WordStatesRecapModalProps> = ({
  isVisible,
  onContinue,
}) => {
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);

  useEffect(() => {
    if (!isVisible) {
      setCountdown(COUNTDOWN_SECONDS); // reset when modal closes
      return;
    }
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, countdown]);

  if (!isVisible) return null;

  return (
    <div
      className="absolute inset-0 z-50 flex items-center justify-center"
      style={{ paddingInline: 32 }}
    >
      {/* Backdrop — only tappable once the countdown elapses (iOS Pressable gate) */}
      <button
        type="button"
        aria-label="Dismiss"
        onClick={countdown > 0 ? undefined : onContinue}
        style={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          border: "none",
          cursor: countdown > 0 ? "default" : "pointer",
        }}
      />

      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 340,
          backgroundColor: colors.white,
          borderRadius: 32,
          padding: 24,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 16,
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <span
            style={{
              fontSize: "var(--base-font-size-large)",
              fontWeight: 700,
              color: colors.text.primary,
              textAlign: "center",
              marginBottom: 8,
            }}
          >
            Word Color Legend
          </span>
          <WordStateRow word="Hola" state="new" label="← New" />
          <WordStateRow word="Hola" state="learning" label="← Learning" />
          <WordStateRow word="Hola" state="known" label="← Known" />
        </div>

        {countdown > 0 && (
          <span
            style={{
              fontSize: "var(--base-font-size-small)",
              color: colors.muted,
              marginBottom: 8,
            }}
          >
            {countdown}...
          </span>
        )}
        <ContinueButton onClick={onContinue} text="Got it!" disabled={countdown > 0} />
      </div>
    </div>
  );
};

export default WordStatesRecapModal;
